import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useMinigameMessages } from '../../hooks/useMinigameMessages';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import { Loader } from '../Loader/Loader';

import styles from './MinigamesWarmFrame.module.scss';

const WARM_LAUNCH_OVERRIDE_PARAM = 'warmMinigame';
const WARM_LAUNCH_FLAG = 'minigames_warm_launch';
const LOADER_DELAY_MS = 400;
const REVEAL_CAP_MS = 3000;
const WARM_IDLE_TIMEOUT_MS = 5000;
const WARM_FALLBACK_DELAY_MS = 3000;

export const useMinigameWarmEnabled = (): boolean => {
  const flagEnabled = useFlag(WARM_LAUNCH_FLAG, false);

  if (flagEnabled) {
    return true;
  }

  return import.meta.env.DEV && new URLSearchParams(window.location.search).get(WARM_LAUNCH_OVERRIDE_PARAM) === '1';
};

export const MinigamesWarmFrame = observer(() => {
  const { minigamesWarmStore } = useStores();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isGameReady, setIsGameReady] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isLoaderShown, setIsLoaderShown] = useState(false);

  const onAuthenticated = useCallback(() => setIsGameReady(true), []);
  const onClosed = useCallback(() => setIsGameReady(false), []);
  const onWarmReady = useCallback((win: Window | null) => minigamesWarmStore.onWarmReady(win), [minigamesWarmStore]);

  useMinigameMessages({ iframeRef, onAuthenticated, onClosed, onWarmReady });

  useEffect(() => {
    if (window.location.pathname.startsWith('/games')) {
      return;
    }

    const start = () => minigamesWarmStore.warm();

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(start, { timeout: WARM_IDLE_TIMEOUT_MS });

      return () => cancelIdleCallback(handle);
    }

    const timer = setTimeout(start, WARM_FALLBACK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [minigamesWarmStore]);

  const { src, isVisible, frameKey } = minigamesWarmStore;

  useEffect(() => {
    if (!isVisible) {
      setIsRevealed(false);

      return;
    }

    if (isGameReady) {
      setIsRevealed(true);

      return;
    }

    const timer = setTimeout(() => setIsRevealed(true), REVEAL_CAP_MS);

    return () => clearTimeout(timer);
  }, [isVisible, isGameReady]);

  useEffect(() => {
    if (!isVisible || isRevealed) {
      setIsLoaderShown(false);

      return;
    }

    const timer = setTimeout(() => setIsLoaderShown(true), LOADER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isVisible, isRevealed]);

  if (!src) {
    return null;
  }

  return (
    <div className={classNames(styles.cover, { [styles.hidden]: !isVisible })}>
      {isLoaderShown && <Loader cover />}
      <iframe
        key={frameKey}
        ref={iframeRef}
        allow="autoplay; clipboard-write *; clipboard-read *"
        className={classNames(styles.frame, { [styles.framePending]: !isRevealed })}
        data-testid="minigames-warm-frame"
        src={src}
        title="Minigames"
      />
    </div>
  );
});
